'use client';

import { useEffect, useCallback, useRef } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import NextLink from 'next/link';
import { Box, Container, Button, Divider } from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { LandingHeader, LandingFooter } from '../../../../features/landing';
import { AuthorInfo } from './AuthorInfo';
import { TableOfContents } from './TableOfContents';
import { BlogContent } from './BlogContent';
import { RelatedPosts } from './RelatedPosts';
import type { BlogPostMeta } from '../lib/posts';

interface BlogLayoutProps {
  slug: string;
  title: string;
  author: string;
  publishDate: string;
  readingTime?: string | undefined;
  content: string;
  relatedPosts?: BlogPostMeta[] | undefined;
}

function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') return '';
  let sessionId = sessionStorage.getItem('blog_session_id');
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem('blog_session_id', sessionId);
  }
  return sessionId;
}

function trackBlogEvent(slug: string, type: 'view' | 'click', locale: string, clickType?: string) {
  const sessionId = getOrCreateSessionId();
  if (!sessionId) return;
  fetch('/api/blog/track', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ slug, type, clickType, locale, sessionId }),
  }).catch(() => {});
}

/**
 * BlogLayout - Full blog post layout with header, TOC, content, CTA, and related posts.
 */
export function BlogLayout({
  slug,
  title,
  author,
  publishDate,
  readingTime,
  content,
  relatedPosts,
}: BlogLayoutProps) {
  const locale = useLocale();
  const t = useTranslations('creator.blog');
  const viewTracked = useRef(false);

  useEffect(() => {
    if (!viewTracked.current) {
      viewTracked.current = true;
      trackBlogEvent(slug, 'view', locale);
    }
  }, [slug, locale]);

  const handleCtaClick = useCallback(() => {
    trackBlogEvent(slug, 'click', locale, 'create_bot');
  }, [slug, locale]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <LandingHeader />

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 }, flex: 1 }}>
        <AuthorInfo
          name={author}
          date={publishDate}
          readingTime={readingTime}
          locale={locale}
        />

        <TableOfContents content={content} title={t('tableOfContents')} />

        <BlogContent>{content}</BlogContent>

        {/* CTA to create a bot */}
        <Box
          sx={{
            textAlign: 'center',
            py: 4,
            my: 4,
            bgcolor: 'primary.50',
            borderRadius: 2,
            border: 1,
            borderColor: 'primary.100',
          }}
        >
          <Button
            component={NextLink}
            href={`/${locale}`}
            variant="contained"
            size="large"
            startIcon={<RocketLaunchIcon />}
            onClick={handleCtaClick}
            sx={{ textTransform: 'none', fontWeight: 600, px: 4 }}
          >
            {t('createBotCta')}
          </Button>
        </Box>

        <Divider sx={{ my: 4 }} />

        {relatedPosts && relatedPosts.length > 0 && (
          <RelatedPosts posts={relatedPosts} title={t('relatedPosts')} />
        )}
      </Container>

      <LandingFooter />
    </Box>
  );
}
